import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import facebookIcon from '@/assets/icons/facebook-icon.svg';
import linkedinIcon from '@/assets/icons/linkedin-icon.svg';
import instagramIcon from '@/assets/icons/instagram-icon.svg';
import googleIcon from '@/assets/icons/post-google.svg';
import { Variant } from '@/components/compound-components/settings/integrations-tab/integrations-tab';
import { useOrganizationContext } from '@/contexts/organization-context';
import {
  PostChannel,
  SchedulingVariantV1Channel,
  SchedulingVariantV1ChannelInput,
} from '@/__generated__/graphql';
import { Button } from '@/components/shadcn/button';
import { gql } from '@/__generated__';
import Image, { ImageProps } from 'next/image';
import { env } from 'next-runtime-env';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { logError } from '@/lib/rum';

export const UpdateSchedulingVariant = gql(`
  mutation UpdateVariantProviderChannel($input: SchedulingVariantV1UpdateInput!) {
    schedulingVariantV1 {
      update(input: $input) {
        ... on SchedulingVariantV1UpdateSuccess {
          result {
            id
            channels {
              channel
              pageId
              name
            }
          }
        }
      }
    }
  }
`);

const isSocialMediaChannel = (
  channel: PostChannel
): channel is Exclude<PostChannel, PostChannel.Unspecified> => {
  return channel !== PostChannel.Unspecified && channel in socialMediaIcons;
};

const socialMediaIcons: {
  Facebook: ImageProps;
  Instagram: ImageProps;
  LinkedIn: ImageProps;
  Google: ImageProps;
} = {
  Facebook: facebookIcon,
  Instagram: instagramIcon,
  LinkedIn: linkedinIcon,
  Google: googleIcon,
};

type DisconnectButtonProps = {
  linkedChannels: SchedulingVariantV1Channel[];
  platformChannels: PostChannel[];
  variant: Variant;
  setLinkedSocialMedia: (channels: SchedulingVariantV1Channel[]) => void;
  platform: 'meta' | 'linkedin' | 'google';
  googleAccount?: string;
  setConnectedGoogleAccount: (gmail: string) => void;
};

const toChannelInput = (
  channel: SchedulingVariantV1Channel
): SchedulingVariantV1ChannelInput => {
  return {
    channel: channel.channel,
    pageId: channel.pageId,
    name: channel.name,
    weight: channel.weight,
    tokenStatus: channel.tokenStatus,
  };
};

export { toChannelInput };

export default function IntegrationDisconnect({
  platformChannels,
  linkedChannels,
  setLinkedSocialMedia,
  variant,
  platform,
  googleAccount,
  setConnectedGoogleAccount,
}: DisconnectButtonProps) {
  const { organizationId } = useOrganizationContext();
  const { t } = useTranslation();
  const [isDisconnect, setIsDisconnect] = useState<string[]>([]);
  const [updateSchedulingVariant] = useMutation(UpdateSchedulingVariant);
  const session = useSessionContext();

  const platformLinkedChannels = linkedChannels.filter(
    (lc: SchedulingVariantV1Channel) =>
      lc.pageId && platformChannels.find((pc: PostChannel) => lc.channel === pc)
  );

  const handleDisconnectClick = async (uniquePageId: string) => {
    if (platform === 'google' && !session.loading) {
      try {
        const response = await fetch(
          `${env('AUTHENTICATION_DOMAIN')}/gsuite/auth/disconnect/${session.userId}`,
          { method: 'DELETE' }
        );

        if (response.status === 204) {
          setConnectedGoogleAccount(''); // Clear the connected Google account
        }
      } catch (error) {
        logError('Failed to disconnect Google account', error);
      } finally {
        setIsDisconnect([]);
      }
      return;
    }

    setIsDisconnect((prevVal) => [...prevVal, uniquePageId]);
  };

  const handleDisconnectCancelClick = (uniquePageId: string) => {
    setIsDisconnect((prevVal) => prevVal.filter((v) => v !== uniquePageId));
  };

  const handleChannelPageRemoval = (pageId: string, channelName: string) => {
    return linkedChannels.map((lc) => {
      if (lc.pageId === pageId && lc.channel === channelName) {
        return { ...lc, name: null, pageId: null, weight: null };
      }

      return { ...lc };
    });
  };

  const handleYesDisconnectClick = async (uniquePageId: string) => {
    const [channelName, pageId] = uniquePageId.split('-');
    const channelPages = handleChannelPageRemoval(pageId, channelName);
    const { variantId } = variant as Variant;

    await updateSchedulingVariant({
      variables: {
        input: {
          where: {
            id: variantId,
            organizationId: organizationId,
          },
          set: {
            channels: channelPages.map(toChannelInput),
          },
        },
      },
    });

    setLinkedSocialMedia(channelPages);
    handleDisconnectCancelClick(uniquePageId);
  };

  return (
    <div className='flex flex-col gap-1'>
      {platform === 'google' && googleAccount && (
        <div className='relative mb-1 flex flex-col justify-between rounded border border-solid border-gray-100 px-3 py-6'>
          <div className='flex items-center gap-2'>
            <Image
              src={socialMediaIcons['Google'].src}
              width={socialMediaIcons['Google'].width}
              height={socialMediaIcons['Google'].height}
              alt={'Disconnect Google Account'}
            />
            <p>{'Google'}</p>
          </div>
          <div className='ml-4 mt-6 border-t border-gray-200'></div>
          <div className='ml-4 mt-6 grid grid-cols-[5fr_2fr] items-center'>
            <p className='text-sm'>{googleAccount}</p>
            <Button
              variant={'maggie-secondary'}
              className='m-0 mt-auto h-8 rounded border-gray-200 py-0 font-medium text-red-700 hover:border-red-700 hover:bg-red-100/30 hover:text-red-700'
              onClick={() => handleDisconnectClick('google')}
            >
              {t('account-menu-settings.disconnect')}
            </Button>
          </div>
        </div>
      )}
      {platformLinkedChannels.map((lc: SchedulingVariantV1Channel) => (
        <div
          key={lc.pageId}
          className='relative mb-1 flex flex-col justify-between rounded border border-solid border-gray-100 px-3 py-6'
        >
          <div className='flex items-center gap-2'>
            {isSocialMediaChannel(lc.channel) &&
              socialMediaIcons[lc.channel]?.src && (
                <Image
                  src={socialMediaIcons[lc.channel]?.src}
                  width={socialMediaIcons[lc.channel]?.width}
                  height={socialMediaIcons[lc.channel]?.height}
                  alt={socialMediaIcons[lc.channel].alt}
                />
              )}
            <p>{lc.channel}</p>
          </div>
          <div className='ml-4 mt-6 border-t border-gray-200'></div>
          <div className='ml-4 mt-6 grid grid-cols-[5fr_2fr] items-center'>
            <p className='text-sm'>
              {lc.name} (ID {lc.pageId})
            </p>
            {!isDisconnect.includes(`${lc.channel}-${lc.pageId}`) ? (
              <Button
                variant={'maggie-secondary'}
                className='m-0 mt-auto h-8 rounded border-gray-200 py-0 font-medium text-red-700 hover:border-red-700 hover:bg-red-100/30 hover:text-red-700'
                onClick={() =>
                  handleDisconnectClick(`${lc.channel}-${lc.pageId}`)
                }
              >
                {t('account-menu-settings.disconnect')}
              </Button>
            ) : (
              <div className='absolute right-3 flex items-center justify-end'>
                <div className='bg-gradient-to-right h-8 w-12 from-white from-30% to-transparent' />
                <p className='bg-white px-5 text-sm'>
                  {t('account-menu-settings.are-you-sure')}
                </p>
                <div className='flex gap-2 bg-white'>
                  <Button
                    variant={'maggie-secondary'}
                    onClick={() =>
                      handleDisconnectCancelClick(`${lc.channel}-${lc.pageId}`)
                    }
                  >
                    {t('account-menu-settings.cancel')}
                  </Button>
                  <Button
                    variant={'destructive'}
                    className='m-0 mt-auto rounded py-0'
                    onClick={() =>
                      handleYesDisconnectClick(`${lc.channel}-${lc.pageId}`)
                    }
                  >
                    {t('account-menu-settings.yes')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
